import { forwardRef } from 'react';

import {
  Box,
  Card,
  CardMedia,
  LinearProgress,
  Typography,
} from '@mui/material';
import { CircleAlert } from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';

interface ImageUploadingCardProps {
  isLoading?: boolean; // Flag to indicate if the image has been uploaded
  imageUrl?: string; // The actual image URL (for existing DB images)
  error?: Error;
  height?: number;
}

export const ImageUploadingCard = forwardRef<
  HTMLDivElement,
  ImageUploadingCardProps & React.PropsWithChildren
>(({ isLoading = false, imageUrl, error, height = 100, children }, ref) => {
  const { t } = useLocale();

  return (
    <Card
      variant="outlined"
      elevation={0}
      sx={{ position: 'relative' }}
      ref={ref}
    >
      {error && (
        <Box
          sx={theme => ({
            position: 'absolute',
            inset: 0,
            padding: 1,
            zIndex: 1,
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(10px)',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            ...theme.applyStyles('dark', {
              background: 'rgba(8, 10, 14, 0.6)',
            }),
          })}
        >
          <Box
            component="span"
            sx={{
              color: 'error.main',
              position: 'absolute',
              zIndex: 2,
              top: 8,
              right: 8,
              display: 'inline-flex',
            }}
          >
            <CircleAlert size={20} />
          </Box>
          <Typography
            variant="body2"
            color="error"
            // One rule, three surfaces: the label an agent reads is translated, the
            // cause is one hover away, and the team gets it through error tracking.
            // Rendering `error.message` here would put untranslated internals
            // ('Upload failed: no signed URL returned') in front of a FR/DE/IT/ES agent.
            title={error.message}
            sx={{ textAlign: 'center' }}
          >
            {t('Upload error')}
          </Typography>
        </Box>
      )}
      <CardMedia sx={{ height }} image={imageUrl}></CardMedia>
      {isLoading && <LinearProgress />}
      {children}
    </Card>
  );
});
